import React from 'react'
import { Box, keyframes } from '@mui/material'

const sweep = keyframes`
  0%   { transform: translateY(0%);   opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { transform: translateY(100%); opacity: 0; }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

/**
 * Viseur de scan entièrement en CSS.
 *
 * Remplace l'ancien PNG de surimpression : une image matricielle est étirée
 * différemment selon le ratio de l'écran (l'ouverture devenait ovale sur les
 * formats 20:9), alors qu'ici la fenêtre reste toujours carrée et se
 * dimensionne d'elle-même : min(72vw, 44vh), bornée entre 180 et 360 px.
 * Elle tient donc aussi bien sur un iPhone SE que sur un grand Android ou en
 * paysage.
 *
 * L'assombrissement du pourtour est obtenu avec un box-shadow géant plutôt
 * qu'avec quatre panneaux : un seul élément, aucun recouvrement à calculer.
 */
export const Viewfinder = ({ color = '#E84E1B', label, active = true }) => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: 'clamp(180px, min(72vw, 44vh), 360px)',
        aspectRatio: '1 / 1',
        borderRadius: '28px',
        boxShadow: '0 0 0 100vmax rgba(6,7,9,0.62)',
        transition: 'box-shadow 240ms ease',
      }}
    >
      {/* Coins d'accroche */}
      {[
        { top: -2, left: -2, borderTop: 4, borderLeft: 4, radius: '26px 0 0 0' },
        { top: -2, right: -2, borderTop: 4, borderRight: 4, radius: '0 26px 0 0' },
        { bottom: -2, left: -2, borderBottom: 4, borderLeft: 4, radius: '0 0 0 26px' },
        { bottom: -2, right: -2, borderBottom: 4, borderRight: 4, radius: '0 0 26px 0' },
      ].map((corner, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 'clamp(28px, 9vw, 46px)',
            height: 'clamp(28px, 9vw, 46px)',
            top: corner.top,
            left: corner.left,
            right: corner.right,
            bottom: corner.bottom,
            borderStyle: 'solid',
            borderColor: color,
            borderWidth: 0,
            borderTopWidth: corner.borderTop || 0,
            borderRightWidth: corner.borderRight || 0,
            borderBottomWidth: corner.borderBottom || 0,
            borderLeftWidth: corner.borderLeft || 0,
            borderRadius: corner.radius,
            animation: active ? `${pulse} 2.4s ease-in-out infinite` : 'none',
          }}
        />
      ))}

      {/* Ligne de balayage */}
      {active ? (
        <Box
          sx={{
            position: 'absolute',
            inset: '6px',
            borderRadius: '22px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '32%',
              width: '100%',
              background: `linear-gradient(180deg, transparent 0%, ${color}22 55%, ${color} 100%)`,
              animation: `${sweep} 2.4s cubic-bezier(0.55, 0, 0.45, 1) infinite`,
            }}
          />
        </Box>
      ) : null}

      {label ? (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 20px)',
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
      ) : null}
    </Box>
  </Box>
)

export default Viewfinder
